/**
 * Vendor service areas and property county from a ZIP.
 * County names come from the 2020 Census county file. The database trigger
 * uses the same 95 percent land-share rule as decideCounty.
 */

const COUNTIES = require('./data/counties.json');

const DOMINANT_SHARE = 0.95;

const STATE_NAMES = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia',
  FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
  IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
  ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota',
  MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada',
  NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York',
  NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon',
  PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota',
  TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia',
  WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
  AS: 'American Samoa', GU: 'Guam', MP: 'Northern Mariana Islands',
  PR: 'Puerto Rico', VI: 'U.S. Virgin Islands',
};

const COUNTY_BY_FIPS = {};
const COUNTIES_BY_STATE = {};
COUNTIES.forEach((county) => {
  COUNTY_BY_FIPS[county.fips] = county;
  if (!COUNTIES_BY_STATE[county.state]) COUNTIES_BY_STATE[county.state] = [];
  COUNTIES_BY_STATE[county.state].push(county);
});

function stateName(code) {
  const state = String(code || '').trim().toUpperCase();
  return STATE_NAMES[state] || state;
}

function shortCountyName(name) {
  return String(name || '')
    .replace(/ City and Borough$/, '')
    .replace(/ Census Area$/, '')
    .replace(/ Municipality$/, '')
    .replace(/ Municipio$/, '')
    .replace(/ County$/, '')
    .replace(/ Parish$/, '')
    .replace(/ Borough$/, '')
    .replace(/ District$/, '');
}

function listOf(value) {
  return Array.isArray(value) ? value : [];
}

function serviceAreaFromInput(input) {
  const body = input || {};
  const rawFips = listOf(body.serviceCountyFips || body.service_county_fips);
  const rawStates = listOf(body.serviceStatewide || body.service_statewide);
  const errors = [];
  const countyFips = [];
  const seenFips = new Set();
  rawFips.forEach((item) => {
    const fips = String(item || '').trim();
    if (!fips || seenFips.has(fips)) return;
    if (!/^\d{5}$/.test(fips) || !COUNTY_BY_FIPS[fips]) {
      errors.push('county');
      return;
    }
    seenFips.add(fips);
    countyFips.push(fips);
  });
  const statewide = [];
  const seenState = new Set();
  rawStates.forEach((item) => {
    const code = String(item || '').trim().toUpperCase();
    if (!code || seenState.has(code)) return;
    if (!/^[A-Z]{2}$/.test(code) || !COUNTIES_BY_STATE[code]) {
      errors.push('state');
      return;
    }
    seenState.add(code);
    statewide.push(code);
  });
  let error = '';
  if (errors.length) error = 'Choose a county from the list';
  else if (countyFips.length === 0 && statewide.length === 0) error = 'Pick at least one county or entire state';
  return { countyFips, statewide, error };
}

function formatServes(record) {
  const area = serviceAreaFromInput(record || {});
  const statewide = new Set(area.statewide);
  const byState = new Map();
  const states = [];
  const addState = (code) => {
    if (!states.includes(code)) states.push(code);
  };
  area.countyFips.forEach((fips) => {
    const county = COUNTY_BY_FIPS[fips];
    if (!county || statewide.has(county.state)) return;
    addState(county.state);
    if (!byState.has(county.state)) byState.set(county.state, []);
    byState.get(county.state).push(shortCountyName(county.name));
  });
  area.statewide.forEach(addState);
  if (states.length === 0) return '';
  if (states.length === 1 && statewide.has(states[0]) && !byState.has(states[0])) {
    return `Serves: All of ${stateName(states[0])}`;
  }
  if (states.length === 1) {
    const names = byState.get(states[0]) || [];
    return `Serves: ${names.join(', ')} (${states[0]})`;
  }
  const parts = states.map((code) => (
    statewide.has(code)
      ? `All of ${stateName(code)}`
      : `${(byState.get(code) || []).join(', ')} (${code})`
  ));
  return `Serves: ${parts.join('; ')}`;
}

function countyLabel(fips) {
  const county = COUNTY_BY_FIPS[String(fips || '').trim()];
  if (!county) return '';
  return `${county.name}, ${county.state}`;
}

function servesCounty(record, countyFips, state) {
  const fips = String(countyFips || '').trim();
  const area = record || {};
  const listed = listOf(area.service_county_fips).map((item) => String(item));
  if (fips && listed.includes(fips)) return true;
  const fromFips = COUNTY_BY_FIPS[fips] && COUNTY_BY_FIPS[fips].state;
  const code = String(state || fromFips || '').trim().toUpperCase();
  return Boolean(code && listOf(area.service_statewide).some((item) => String(item).trim().toUpperCase() === code));
}

function sortVendorsByCounty(vendors, countyFips, state) {
  const rows = Array.isArray(vendors) ? vendors.slice() : [];
  if (!countyFips && !state) return rows;
  return rows.sort((left, right) => (
    Number(servesCounty(right, countyFips, state)) - Number(servesCounty(left, countyFips, state))
  ));
}

function decideCounty({
  zip,
  state,
  candidates,
  pick,
  source,
  zipChanged,
  previousSource,
  previousFips,
} = {}) {
  const normalized = String(zip || '').replace(/\D/g, '').slice(0, 5);
  const st = /^[A-Za-z]{2}$/.test(String(state || '').trim())
    ? String(state).trim().toUpperCase()
    : '';
  const rows = (Array.isArray(candidates) ? candidates : [])
    .filter((row) => row && (!st || row.state === st));
  const chosen = String(pick || '').trim();
  const prior = String(previousFips || '').trim();

  if (!zipChanged && previousSource === 'picked' && prior && chosen === prior) {
    const topShare = rows.reduce((best, row) => Math.max(best, Number(row.land_share) || 0), 0);
    const dominant = rows.length === 1 || topShare >= DOMINANT_SHARE;
    return { fips: prior, source: 'picked', mode: rows.length > 1 && !dominant ? 'split' : 'chosen' };
  }

  if (normalized.length < 5) {
    if (chosen) return { fips: chosen, source: 'picked', mode: 'pick' };
    return { fips: '', source: '', mode: 'missing-zip' };
  }
  if (rows.length === 0) {
    if (chosen) return { fips: chosen, source: 'picked', mode: 'pick' };
    return { fips: '', source: '', mode: 'unknown' };
  }

  const top = rows.slice().sort((a, b) => Number(b.land_share) - Number(a.land_share) || String(a.fips).localeCompare(String(b.fips)))[0];
  const dominant = rows.length === 1 || Number(top.land_share) >= DOMINANT_SHARE;
  const pickOk = rows.some((row) => String(row.fips) === chosen);

  if (dominant) {
    if (source === 'picked' && pickOk && chosen !== String(top.fips)) {
      return { fips: chosen, source: 'picked', mode: 'chosen' };
    }
    if (!zipChanged && previousSource === 'picked' && prior) {
      return { fips: prior, source: 'picked', mode: 'chosen' };
    }
    return { fips: String(top.fips), source: 'zip', mode: 'auto' };
  }

  if (pickOk) return { fips: chosen, source: 'picked', mode: 'split' };
  if (!zipChanged && previousSource === 'picked' && rows.some((row) => String(row.fips) === prior)) {
    return { fips: prior, source: 'picked', mode: 'split' };
  }
  return { fips: '', source: '', mode: 'split', choices: rows };
}

function statesForPicker() {
  const rest = Object.keys(COUNTIES_BY_STATE)
    .filter((code) => code !== 'FL')
    .sort((left, right) => stateName(left).localeCompare(stateName(right)));
  return ['FL', ...rest];
}

module.exports = {
  DOMINANT_SHARE,
  STATE_NAMES,
  COUNTY_BY_FIPS,
  COUNTIES_BY_STATE,
  stateName,
  shortCountyName,
  serviceAreaFromInput,
  formatServes,
  countyLabel,
  servesCounty,
  sortVendorsByCounty,
  decideCounty,
  statesForPicker,
};
